import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAdminApps, fetchRoleMaps, fetchRoleMapSyncStatus } from '@/api/console';
import { fetchOrganizations } from '@/api/orgAdmin';
import { EmptyState, ErrorState, MetricCard, StatusTag } from '@/components/ui';
import type { StatusConfig } from '@/components/ui';

/**
 * EAA-1776/1784: `/console` landing — Thư mục (EMG Directory).
 *
 * Operator-facing overview built from real endpoints: three summary cards
 * (organizations / apps / role-map health) that link into the matching screen,
 * then the standard-role→per-app mapping table and the integrating-apps table.
 * EAA-1784 dropped the internal entity-schema diagram (Assignment/Level/… model
 * names) — it meant nothing to an operator and half of it was unpopulated.
 *
 * Gating (§1b): the page mounts only with `console.directory.view`; every query
 * sets `enabled` on the same capability so hidden data is never fetched.
 */
export function DirectoryPage() {
    const can = useCan();
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();

    // EAA-1804: local status vocabulary for the apps table's "Trạng thái" column —
    // StatusTag itself stays domain-agnostic, so the enabled/disabled → label/tone
    // mapping lives here on the page.
    // EAA-1814: `enabled` is the production-approval gate, not a liveness signal, so the pair
    // reads duyệt/chưa duyệt. "Đang chạy" beside "Chưa duyệt" also mixed two different axes in
    // one column.
    // EAA-1845: built inside the component (rather than at module scope) because
    // the labels now go through `t()`.
    const appStatusConfig: Record<string, StatusConfig> = {
        enabled: { label: t('console.directory.statusApproved'), tone: 'success' },
        disabled: { label: t('console.directory.statusNotApproved'), tone: 'default' },
    };

    const enabled = authenticated && Boolean(accessToken) && can('console.directory.view');
    const appsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const syncStatusQuery = useQuery({
        queryKey: ['role-map-sync-status', accessToken],
        queryFn: () => fetchRoleMapSyncStatus(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    // EAA-1818: root organizations only — this card's `to` is `/organizations`,
    // the registry, which lists roots. Omitting `parent_id` counted child đơn vị
    // as organizations too, so the tile disagreed with the screen it opens.
    const orgsQuery = useQuery({
        queryKey: ['organizations', accessToken, 'roots'],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, { parentId: null }),
        enabled,
        retry: false,
    });

    // §1b render gate: without the capability this component does not mount —
    // no empty shell, and none of the queries above ever ran (enabled=false).
    if (!can('console.directory.view')) {
        return null;
    }

    const apps = appsQuery.data ?? [];
    const roleMaps = roleMapsQuery.data ?? [];
    const orgCount = orgsQuery.data?.count ?? 0;
    const enabledApps = apps.filter((a) => a.enabled).length;
    const sync = syncStatusQuery.data;
    const degraded = sync?.status === 'degraded';
    const totalMappings = (sync?.healthy_count ?? 0) + (sync?.paused_count ?? 0);

    // Standard role keys → per-app chips (defaults only; org overrides are on
    // the Role map screen — this is the at-a-glance vocabulary view).
    const byRoleKey = new Map<
        string,
        { application: string; app_role: string | null; healthy: boolean }[]
    >();
    for (const row of roleMaps) {
        if (row.organization_id) continue;
        const list = byRoleKey.get(row.role_key) ?? [];
        list.push({
            application: row.application,
            app_role: row.app_role,
            healthy: row.app_role_active,
        });
        byRoleKey.set(row.role_key, list);
    }
    const mappingCountByApp = new Map<string, number>();
    for (const row of roleMaps) {
        mappingCountByApp.set(row.application, (mappingCountByApp.get(row.application) ?? 0) + 1);
    }

    return (
        <main className="portal-admin portal-console-page">
            <header className="portal-admin-header">
                <h1>{t('console.directory.title')}</h1>
            </header>
            <p className="portal-admin-hint">{t('console.directory.hint')}</p>

            {/* Tổng quan — real counts, each card links into the matching screen. */}
            <section className="portal-admin-section">
                <h2>{t('console.directory.overviewTitle')}</h2>
                <div className="portal-console-stats">
                    <MetricCard
                        to="/organizations"
                        value={orgCount}
                        label={t('console.directory.orgsLabel')}
                        sub={t('console.directory.orgsSub')}
                    />
                    <MetricCard
                        to="/console/apps"
                        value={enabledApps}
                        valueSuffix={` / ${apps.length}`}
                        // EAA-1814: was "Ứng dụng đang chạy", which this number cannot mean.
                        // `Application.enabled` is the production-approval gate (D11) — an operator
                        // flipped it on — and the model carries no uptime, heartbeat or health
                        // field at all. "đang chạy" promised liveness the backend never measures.
                        label={t('console.directory.appsApprovedLabel')}
                        sub={t('console.directory.appsManageSub')}
                    />
                    <MetricCard
                        to="/console/role-map"
                        value={sync?.healthy_count ?? 0}
                        valueSuffix={` / ${totalMappings}`}
                        // EAA-1814: "khỏe mạnh" is the word for a *person's* health; systems take
                        // "tình trạng". The glossary also bans "Ánh xạ" from visible copy. The ⚠
                        // glyph goes with it — the handoff forbids emoji, and the alert tone
                        // already carries the severity.
                        label={t('console.directory.roleMapNormalLabel')}
                        tone={degraded ? 'alert' : 'ok'}
                        sub={
                            degraded
                                ? t('console.directory.roleMapPausedSub', {
                                      count: sync?.paused_count,
                                  })
                                : t('console.directory.roleMapAllNormalSub')
                        }
                    />
                </div>
            </section>

            <section className="portal-admin-section">
                <h2>{t('console.directory.roleTableTitle')}</h2>
                <p className="portal-admin-hint">
                    {t('console.directory.roleTableHintBefore')} <code>teacher</code>
                    {t('console.directory.roleTableHintAfter')}
                </p>
                {roleMapsQuery.isLoading && (
                    <p aria-busy="true">{t('console.directory.loadingRoleMaps')}</p>
                )}
                {roleMapsQuery.isSuccess && byRoleKey.size === 0 && (
                    <EmptyState title={t('console.directory.emptyRoleMaps')} />
                )}
                {byRoleKey.size > 0 && (
                    <div className="portal-table-scroll">
                        <table className="portal-admin-table">
                            <thead>
                                <tr>
                                    <th>{t('console.directory.columnRoleKey')}</th>
                                    <th>{t('console.directory.columnRoleTargets')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...byRoleKey.entries()].map(([roleKey, targets]) => (
                                    <tr key={roleKey}>
                                        <td>
                                            <code>{roleKey}</code>
                                        </td>
                                        <td>
                                            <span className="portal-console-chips">
                                                {targets.map((target, i) => (
                                                    <span
                                                        key={`${target.application}-${target.app_role}-${i}`}
                                                        className={
                                                            'portal-console-chip' +
                                                            (target.healthy ? '' : ' is-paused')
                                                        }
                                                        title={
                                                            target.healthy
                                                                ? t('console.directory.chipHealthy')
                                                                : t('console.directory.chipPaused')
                                                        }
                                                    >
                                                        {target.application}: {target.app_role ?? '—'}
                                                    </span>
                                                ))}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="portal-admin-section">
                <h2>{t('console.directory.appsTableTitle')}</h2>
                {appsQuery.isLoading && (
                    <p aria-busy="true">{t('console.directory.loadingApps')}</p>
                )}
                {appsQuery.isError && (
                    <ErrorState
                        description={t('console.directory.appsError')}
                        onRetry={() => appsQuery.refetch()}
                        retrying={appsQuery.isFetching}
                    />
                )}
                {appsQuery.isSuccess && (
                    <div className="portal-table-scroll">
                        <table className="portal-admin-table">
                            <thead>
                                <tr>
                                    <th>{t('console.directory.columnCode')}</th>
                                    <th>{t('console.directory.columnName')}</th>
                                    <th>{t('console.directory.columnAudience')}</th>
                                    <th>{t('console.directory.columnApiBase')}</th>
                                    <th>{t('console.directory.columnMappingCount')}</th>
                                    <th>{t('console.directory.columnStatus')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apps.map((app) => (
                                    <tr
                                        key={app.code}
                                        className={app.enabled ? '' : 'portal-console-row-muted'}
                                    >
                                        <td>
                                            <code>{app.code}</code>
                                        </td>
                                        <td>{app.name}</td>
                                        <td>{app.audience || '—'}</td>
                                        <td>{app.api_base || '—'}</td>
                                        <td>{mappingCountByApp.get(app.code) ?? 0}</td>
                                        <td>
                                            <StatusTag
                                                status={app.enabled ? 'enabled' : 'disabled'}
                                                config={appStatusConfig}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </main>
    );
}
